import { AutoSelection, BlockStack, Combobox, Listbox, Tag, Text } from "@shopify/polaris";
import { useCallback, useEffect, useMemo, useState } from "react";

interface CustomerTagExclusionProps {
  customerTagsBlocked: string[] | undefined;
  onChange: (customerTagsBlocked: string[]) => void;
}

export const CustomerTagExclusion = ({ customerTagsBlocked, onChange }: CustomerTagExclusionProps) => {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [value, setValue] = useState("");
  const [, setSuggestion] = useState("");

  useEffect(() => {
    if (customerTagsBlocked) {
      setSelectedTags(customerTagsBlocked);
    }
  }, [customerTagsBlocked]);

  const handleActiveOptionChange = useCallback(
    (activeOption: string) => {
      const activeOptionIsAction = activeOption === value;

      if (!activeOptionIsAction && !selectedTags.includes(activeOption)) {
        setSuggestion(activeOption);
      } else {
        setSuggestion("");
      }
    },
    [value, selectedTags],
  );
  const updateSelection = useCallback(
    (selected: string) => {
      const nextSelectedTags = new Set([...selectedTags]);

      if (nextSelectedTags.has(selected)) {
        nextSelectedTags.delete(selected);
      } else {
        nextSelectedTags.add(selected);
      }
      setSelectedTags([...nextSelectedTags]);
      onChange([...nextSelectedTags]);
      setValue("");
      setSuggestion("");
    },
    [selectedTags],
  );

  const removeTag = useCallback(
    (tag: string) => () => {
      updateSelection(tag);
    },
    [updateSelection],
  );

  const getAllTags = useCallback(() => {
    const savedTags: string[] = [];
    return [...new Set([...savedTags, ...selectedTags].sort())];
  }, [selectedTags]);

  const formatOptionText = useCallback(
    (option: string) => {
      const trimValue = value.trim().toLocaleLowerCase();
      const matchIndex = option.toLocaleLowerCase().indexOf(trimValue);

      if (!value || matchIndex === -1) return option;

      const start = option.slice(0, matchIndex);
      const highlight = option.slice(matchIndex, matchIndex + trimValue.length);
      const end = option.slice(matchIndex + trimValue.length, option.length);

      return (
        <p>
          {start}
          <Text as="span" fontWeight="bold">
            {highlight}
          </Text>
          {end}
        </p>
      );
    },
    [value],
  );

  const escapeSpecialRegExCharacters = useCallback((value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), []);

  const options = useMemo(() => {
    let list;
    const allTags = getAllTags();
    const filterRegex = new RegExp(escapeSpecialRegExCharacters(value), "i");

    if (value) {
      list = allTags.filter((tag) => tag.match(filterRegex));
    } else {
      list = allTags;
    }

    return [...list];
  }, [value, getAllTags, escapeSpecialRegExCharacters]);

  const verticalContentMarkup =
    selectedTags.length > 0 ? (
      <BlockStack align="center" gap="0">
        {selectedTags.map((tag) => (
          <Tag key={`option-${tag}`} onRemove={removeTag(tag)}>
            {tag}
          </Tag>
        ))}
      </BlockStack>
    ) : null;

  const optionMarkup =
    options.length > 0
      ? options.map((option) => {
          return (
            <Listbox.Option
              accessibilityLabel={option}
              key={option}
              selected={selectedTags.includes(option)}
              value={option}
            >
              <Listbox.TextOption selected={selectedTags.includes(option)}>
                {formatOptionText(option)}
              </Listbox.TextOption>
            </Listbox.Option>
          );
        })
      : null;

  const noResults = value && !getAllTags().includes(value);

  const actionMarkup = noResults ? <Listbox.Action value={value}>{`Add "${value}"`}</Listbox.Action> : null;

  const listboxMarkup =
    optionMarkup || actionMarkup ? (
      <Listbox
        autoSelection={AutoSelection.None}
        onActiveOptionChange={handleActiveOptionChange}
        onSelect={updateSelection}
      >
        {actionMarkup}
      </Listbox>
    ) : null;

  return (
    <div>
      <Combobox
        activator={
          <Combobox.TextField
            autoComplete="off"
            label=""
            onChange={setValue}
            placeholder="Add tags"
            value={value}
            verticalContent={verticalContentMarkup}
          />
        }
        allowMultiple
      >
        {listboxMarkup}
      </Combobox>
    </div>
  );
};
